import { useMemo } from 'react';
import {
  useGetProfilesByUserIdQuery,
  useGetTripsByUserByUserIdQuery,
} from '../../store/backendApi';
import { getTripUrl, getTripHeaderUrl } from '../../utils/profile';

export interface ProfileTrip {
  title: string;
  href: string;
  imageUrl: string;
  startDate: string;
  endDate: string;
  count: string;
  role: string;
  ownerId: string;
}

/**
 * Platform-agnostic hook for managing profile page data.
 * Handles fetching profile and trips data, and transforming them for display.
 */
export function useProfileData(userId: string | undefined) {
  const {
    data: profileData,
    isLoading: profileLoading,
    isError: profileLoadError,
  } = useGetProfilesByUserIdQuery(userId ?? '', {
    skip: !userId,
  });

  const {
    data: tripsData,
    isLoading: tripsLoading,
    isError: tripsLoadError,
  } = useGetTripsByUserByUserIdQuery(userId ?? '', {
    skip: !userId,
  });

  // Transform trips for display
  const transformedTrips = useMemo((): ProfileTrip[] | undefined => {
    if (!tripsData || !profileData) return undefined;

    return tripsData.map((trip) => ({
      title: trip.title,
      href: getTripUrl(trip.id, profileData.username, trip.shareMode, trip.slug, trip.linkSecret),
      imageUrl: getTripHeaderUrl(trip.id, trip.headerFileName),
      startDate: trip.startDate,
      endDate: trip.endDate,
      count: trip.postCount.toString(),
      role: '',
      ownerId: userId ?? '',
    }));
  }, [tripsData, profileData, userId]);

  // Profile description with fallback
  const profileDescription = useMemo(() => {
    if (!profileData) return '';
    return profileData.description.trim() === ''
      ? 'No profile description.'
      : profileData.description;
  }, [profileData]);

  return {
    profile: profileData,
    trips: transformedTrips,
    profileDescription,
    isLoading: profileLoading || tripsLoading,
    error: profileLoadError || tripsLoadError,
    hasTrips: (transformedTrips?.length ?? 0) > 0,
  };
}
